import { useCallback, useEffect, useState } from 'react'
import {
  markUserNotificationAsRead,
  observeUserNotifications,
} from '@domain/features/user-notifications/user-notifications-feature'
import type { UserNotification } from '@domain/models/user-notifications'
import { useAuthUser } from '@presentation/hooks/useAuthUser'

export function useUserNotifications() {
  const { user, isAuthenticated, loading: authLoading } = useAuthUser()
  const [notifications, setNotifications] = useState<UserNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [markingAsReadIds, setMarkingAsReadIds] = useState<string[]>([])

  useEffect(() => {
    setError(null)

    if (authLoading) {
      setLoading(true)
      return
    }

    if (!isAuthenticated || !user?.uid) {
      setNotifications([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    const unsubscribe = observeUserNotifications(
      (items) => {
        if (cancelled) {
          return
        }
        setNotifications(items)
        setLoading(false)
      },
      (nextError) => {
        if (cancelled) {
          return
        }
        setError(nextError.message)
        setLoading(false)
      },
      user.uid,
    )

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [authLoading, isAuthenticated, user?.uid])

  const markAsRead = useCallback(
    async (notificationId: string): Promise<boolean> => {
      if (!isAuthenticated) {
        return false
      }

      setMarkingAsReadIds((current) =>
        current.includes(notificationId) ? current : [...current, notificationId])
      try {
        setError(null)
        await markUserNotificationAsRead(notificationId)
        return true
      } catch (nextError) {
        setError(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to mark notification as read.',
        )
        return false
      } finally {
        setMarkingAsReadIds((current) => current.filter((id) => id !== notificationId))
      }
    },
    [isAuthenticated],
  )

  const isMarkingAsRead = useCallback(
    (notificationId: string) => markingAsReadIds.includes(notificationId),
    [markingAsReadIds],
  )

  return {
    notifications,
    loading: loading || authLoading,
    error,
    isAuthenticated,
    markAsRead,
    isMarkingAsRead,
  }
}
